import { VehicleSurveyImage } from "../interfaces/altair";
import { apiFetch } from "../utils/api";

// ═══════════════════════════════════════════════════════════
// INTERFACES
// ═══════════════════════════════════════════════════════════

export interface PolicyInquiryResponse {
  found: boolean;
  policyNumber: string;
  policyHolderName: string;
  vehiclePlateNumber: string;
  vehicleMake: string;
  vehicleModel: string;
  policyStartDate: string;
  policyEndDate: string;
  claimRequestNum: string;
}

export interface ContractedWorkshop {
  workshopId: number;
  workshopName: string;
  workshopNameEn: string;
  address: string;
  city: string;
  phoneNumber: string;
  lat: number;
  long: number;
  distance?: number; // Distance in meters
  isNearest: boolean;
}

export interface ContractedWorkshopsResponse {
  workshops: ContractedWorkshop[];
}

export interface NonContractedWorkshopDetails {
  city: string;
  area: string;
  workshopAddress: string;
  workshopName: string;
  contactPersonName: string;
  mobile: string;
  phoneNumber: string;
  hotline: string;
}

export interface ChooseWorkshopResponse {
  success: boolean;
  message: string;
}

export interface SubmitClaimSurveyResponse {
  claimSurveyId: string;
  message: string;
}

// ═══════════════════════════════════════════════════════════
// API FUNCTIONS
// ═══════════════════════════════════════════════════════════

/**
 * Searches for a policy by policy number
 * Returns policy details if found
 */
export const policyInquiry = async (
  policyNumber: string
): Promise<PolicyInquiryResponse> => {
  const response = await apiFetch("/Policy/inquiry", {
    method: "POST",
    body: JSON.stringify({ policyNumber }),
  });

  if (!response.ok) {
    let errorMessage = `Backend error while searching for policy: ${response.status} ${response.statusText}`;
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorMessage;
    } catch {
      // Use default error message if JSON parsing fails
    }
    throw new Error(errorMessage);
  }

  const data = await response.json();
  return {
    found: data.found || data.Found || false,
    policyNumber: data.policyNumber || data.PolicyNumber || "",
    policyHolderName: data.policyHolderName || data.PolicyHolderName || "",
    vehiclePlateNumber: data.vehiclePlateNumber || data.VehiclePlateNumber || "",
    vehicleMake: data.vehicleMake || data.VehicleMake || "",
    vehicleModel: data.vehicleModel || data.VehicleModel || "",
    policyStartDate: data.policyStartDate || data.PolicyStartDate || "",
    policyEndDate: data.policyEndDate || data.PolicyEndDate || "",
    claimRequestNum: data.claimRequestNum || data.ClaimRequestNum || "",
  };
};

/**
 * Gets the list of contracted workshops with distance calculation from user location
 * @param lat User's latitude
 * @param long User's longitude
 */
export const getContractedWorkshops = async (
  lat: number,
  long: number
): Promise<ContractedWorkshop[]> => {
  const response = await apiFetch("/Policy/contracted-workshops", {
    method: "POST",
    body: JSON.stringify({
      lat,
      long,
    }),
  });

  if (!response.ok) {
    let errorMessage = `Backend error while getting contracted workshops: ${response.status} ${response.statusText}`;
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorMessage;
    } catch {
      // Use default error message if JSON parsing fails
    }
    throw new Error(errorMessage);
  }

  const data = await response.json();
  const workshops = data.workshops || data.Workshops || [];
  
  return workshops.map((w: any) => ({
    workshopId: w.workshopId || w.WorkshopId || 0,
    workshopName: w.workshopName || w.WorkshopName || "",
    workshopNameEn: w.workshopNameEn || w.WorkshopNameEn || "",
    address: w.address || w.Address || "",
    city: w.city || w.City || "",
    phoneNumber: w.phoneNumber || w.PhoneNumber || "",
    lat: w.lat || w.Lat || 0,
    long: w.long || w.Long || 0,
    distance: w.distance !== undefined ? w.distance : w.Distance,
    isNearest: w.isNearest || w.IsNearest || false,
  }));
};

/**
 * Submits the selection of a contracted workshop
 */
export const chooseContractedWorkshop = async (
  workshopId: number,
  policyRequestRef: string,
  claimRequestNum: string
): Promise<ChooseWorkshopResponse> => {
  const response = await apiFetch("/Policy/choose-contracted-workshop", {
    method: "POST",
    body: JSON.stringify({
      workshopId,
      policyRequestRef,
      claimRequestNum,
    }),
  });

  if (!response.ok) {
    let errorMessage = `Backend error while selecting workshop: ${response.status} ${response.statusText}`;
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorMessage;
    } catch {
      // Use default error message if JSON parsing fails
    }
    throw new Error(errorMessage);
  }

  const data = await response.json();
  return {
    success: data.success || data.Success || false,
    message: data.message || data.Message || "Workshop selected successfully",
  };
};

/**
 * Submits non-contracted workshop details
 */
export const chooseNonContractedWorkshop = async (
  workshopDetails: NonContractedWorkshopDetails,
  policyRequestRef: string,
  claimRequestNum: string
): Promise<ChooseWorkshopResponse> => {
  const response = await apiFetch("/Policy/choose-non-contracted-workshop", {
    method: "POST",
    body: JSON.stringify({
      workshopDetails: {
        City: workshopDetails.city,
        Area: workshopDetails.area,
        WorkshopAddress: workshopDetails.workshopAddress,
        WorkshopName: workshopDetails.workshopName,
        ContactPersonName: workshopDetails.contactPersonName,
        Mobile: workshopDetails.mobile,
        PhoneNumber: workshopDetails.phoneNumber,
        Hotline: workshopDetails.hotline,
      },
      policyRequestRef,
      claimRequestNum,
    }),
  });

  if (!response.ok) {
    let errorMessage = `Backend error while submitting workshop details: ${response.status} ${response.statusText}`;
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorMessage;
    } catch {
      // Use default error message if JSON parsing fails
    }
    throw new Error(errorMessage);
  }

  const data = await response.json();
  return {
    success: data.success || data.Success || false,
    message: data.message || data.Message || "Workshop details submitted successfully",
  };
};

/**
 * Submits the claim survey with vehicle images and description
 * Used for both live survey and manual image upload
 */
export const submitClaimSurvey = async (
  policyNumber: string,
  images: VehicleSurveyImage[],
  damageDescription: string
): Promise<SubmitClaimSurveyResponse> => {
  const response = await apiFetch("/Policy/submit-claim-survey", {
    method: "POST",
    body: JSON.stringify({
      PolicyNumber: policyNumber,
      Description: damageDescription,
      Photos: images.map((img) => ({
        DocumentName: img.side + ".jpeg",
        Side: img.side,
        Data: img.data.startsWith("data:")
          ? img.data
          : `data:image/jpeg;base64,${img.data}`,
      })),
    }),
  });

  if (!response.ok) {
    let errorMessage = `Backend error while submitting claim survey: ${response.status} ${response.statusText}`;
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorMessage;
    } catch {
      // Use default error message if JSON parsing fails
    }
    throw new Error(errorMessage);
  }

  const data = await response.json();
  return {
    claimSurveyId: data.claimSurveyId || data.ClaimSurveyId || "",
    message: data.message || data.Message || "Claim survey submitted successfully",
  };
};


